/**
 * Publisher payout overrides — TanStack Query hooks.
 *
 * Thin binding layer between the page component and the typed API in `./api`.
 * All mutations invalidate the `['publisher-payout-overrides']` root key so
 * any live list view refreshes after create / update / delete. Operators can
 * expect the table to reflect writes without a manual refetch.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationOptions,
	type UseQueryOptions,
} from "@tanstack/react-query";
import {
	createOverride,
	deleteOverride,
	listOverrides,
	overrideQueryKeys,
	updateOverride,
	type CreateOverrideInput,
	type ListOverridesResponse,
	type OverrideRow,
	type UpdateOverrideInput,
} from "./api";

// ---------------------------------------------------------------------------
// Query hook — fetch overrides for one affiliate.
// ---------------------------------------------------------------------------

export interface UsePublisherPayoutOverridesListOptions {
	affiliateId: string;
	enabled?: boolean;
}

export function usePublisherPayoutOverridesList(
	options: UsePublisherPayoutOverridesListOptions,
	queryOptions?: Omit<
		UseQueryOptions<ListOverridesResponse, Error>,
		"queryKey" | "queryFn" | "enabled"
	>,
) {
	const affiliateId = options.affiliateId.trim();
	const enabled = (options.enabled ?? true) && affiliateId.length > 0;
	return useQuery<ListOverridesResponse, Error>({
		queryKey: overrideQueryKeys.list(affiliateId),
		queryFn: () => listOverrides(affiliateId),
		enabled,
		...queryOptions,
	});
}

// ---------------------------------------------------------------------------
// Create mutation.
// ---------------------------------------------------------------------------

export function usePublisherPayoutOverridesCreate(
	options?: UseMutationOptions<
		{ override: OverrideRow },
		Error,
		CreateOverrideInput
	>,
) {
	const qc = useQueryClient();
	return useMutation<{ override: OverrideRow }, Error, CreateOverrideInput>({
		mutationFn: createOverride,
		...options,
		onSuccess: (data, vars, onMutateResult, ctx) => {
			qc.invalidateQueries({ queryKey: overrideQueryKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
}

// ---------------------------------------------------------------------------
// Update mutation.
// ---------------------------------------------------------------------------

export interface UpdateOverrideArgs {
	id: string;
	input: UpdateOverrideInput;
}

export function usePublisherPayoutOverridesUpdate(
	options?: UseMutationOptions<
		{ override: OverrideRow },
		Error,
		UpdateOverrideArgs
	>,
) {
	const qc = useQueryClient();
	return useMutation<{ override: OverrideRow }, Error, UpdateOverrideArgs>({
		mutationFn: ({ id, input }) => updateOverride(id, input),
		...options,
		onSuccess: (data, vars, onMutateResult, ctx) => {
			qc.invalidateQueries({ queryKey: overrideQueryKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
}

// ---------------------------------------------------------------------------
// Delete mutation (single id).
// ---------------------------------------------------------------------------

export function usePublisherPayoutOverridesDelete(
	options?: UseMutationOptions<
		{ deleted: boolean; id: string },
		Error,
		string
	>,
) {
	const qc = useQueryClient();
	return useMutation<{ deleted: boolean; id: string }, Error, string>({
		mutationFn: deleteOverride,
		...options,
		onSuccess: (data, vars, onMutateResult, ctx) => {
			qc.invalidateQueries({ queryKey: overrideQueryKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
}

// ---------------------------------------------------------------------------
// Bulk delete — sequential DELETEs, then single invalidation.
// ---------------------------------------------------------------------------

export interface BulkDeleteResult {
	requested: number;
	succeeded: string[];
	failed: Array<{ id: string; error: string }>;
}

export function usePublisherPayoutOverridesBulkDelete(
	options?: UseMutationOptions<BulkDeleteResult, Error, readonly string[]>,
) {
	const qc = useQueryClient();
	return useMutation<BulkDeleteResult, Error, readonly string[]>({
		mutationFn: async (ids) => {
			const succeeded: string[] = [];
			const failed: Array<{ id: string; error: string }> = [];
			for (const id of ids) {
				try {
					await deleteOverride(id);
					succeeded.push(id);
				} catch (err) {
					failed.push({
						id,
						error: err instanceof Error ? err.message : String(err),
					});
				}
			}
			return { requested: ids.length, succeeded, failed };
		},
		...options,
		onSuccess: (data, vars, onMutateResult, ctx) => {
			qc.invalidateQueries({ queryKey: overrideQueryKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
}
